import { useEffect, useState } from "react";
import { getUserType } from "util/auth";
import {
  Category,
  OrderLargeClassification,
  TyreOptions,
  WheelOptions,
  StorehouseSupplierOptionsWithBlank,
  OrderStatus,
  OrderReceiptStatus,
  OrderStatusObject,
  Supplier,
} from "features/order/types";
import getTyreOptionsApi from "../api/getTyreOptionsApi";

/**
 * 注文検索hooks
 * @param isInitFetch 最初にフェッチするかどうか（未ログインの場合などはしない）
 * @returns state
 */
export default function useJoyfulSearch(isInitFetch: boolean) {
  const [orderNumber, setOrderNumber] = useState("");
  const [productCd, setProductCd] = useState("");
  const [productName, setProductName] = useState("");
  const [brand, setBrand] = useState("");
  const [janCd, setJanCd] = useState("");
  const [productNumber, setProductNumber] = useState("");
  const [tyreWidth, setTyreWidth] = useState<number | undefined>(undefined);
  const [tyreRatio, setTyreRatio] = useState<number | undefined>(undefined);
  const [tyreRimDiam, setTyreRimDiam] = useState<number | undefined>(undefined);
  const [wheelWidth, setWheelWidth] = useState<number | undefined>(undefined);
  const [wheelPcd, setWheelPcd] = useState<number | undefined>(undefined);
  const [wheelHole, setWheelHole] = useState<number | undefined>(undefined);
  const [wheelRimDiam, setWheelRimDiam] = useState<number | undefined>(undefined);
  // 商品コード検索用
  const [tyreWidthCd, setTyreWidthCd] = useState<number | undefined>(undefined);
  const [tyreRatioCd, setTyreRatioCd] = useState<number | undefined>(undefined);
  const [tyreRimDiamCd, setTyreRimDiamCd] = useState<number | undefined>(undefined);
  const [wheelWidthCd, setWheelWidthCd] = useState<number | undefined>(undefined);
  const [wheelPcdCd, setWheelPcdCd] = useState<number | undefined>(undefined);
  const [wheelHoleCd, setWheelHoleCd] = useState<number | undefined>(undefined);
  const [wheelRimDiamCd, setWheelRimDiamCd] = useState<number | undefined>(undefined);
  const [shopCd, setShopCd] = useState<string>(localStorage.getItem("shop_cd") ?? "");
  const [classification, setClassification] = useState("");
  const [storehouse, setStorehouse] = useState("");
  const [supplier, setSupplier] = useState(""); // 仕入先名
  const [tyreOptions, setTyreOptions] = useState<TyreOptions>({
    tyreWidthList: [],
    tyreRatioList: [],
    tyreRimDiamList: [],
  });
  const [wheelOptions, setWheelOptions] = useState<WheelOptions>({
    wheelWidthList: [],
    wheelPcdList: [],
    wheelHoleList: [],
    wheelRimDiamList: [],
  });
  const [classificationOptions, setClassificationOptions] = useState<OrderLargeClassification[]>(
    [],
  );
  const [brandOptions, setBrandOptions] = useState<string[]>([]);
  const [supplierOptions, setSupplierOptions] = useState<Supplier[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // 追加
  const [isCustomerOrder, setIsCustomerOrder] = useState(false);
  const [shopName, setShopName] = useState("");
  const [orderStatus, setOrderStatus] = useState<OrderStatus>("");
  const [orderReceiptStatus, setOrderReceiptStatus] = useState<OrderReceiptStatus>("");
  const [storeOrderer, setStoreOrderer] = useState("");
  const [approver, setApprover] = useState("");
  const [orderStartDate, setOrderStartDate] = useState("");
  const [orderEndDate, setOrderEndDate] = useState("");
  const [companyCdField, setCompanyCdField] = useState(""); // 検索フィールドに入力した企業コード。企業ユーザーの場合にcompanyCdを使っているため、例外的にこの名前にした。
  const [companyName, setCompanyName] = useState("");
  const [centerCd, setCenterCd] = useState("");
  const [orderStatusOptions, setOrderStatusOptions] = useState<OrderStatusObject[]>([]);

  useEffect(() => {
    if (isInitFetch) {
      (async () => {
        setIsLoading(true);
        try {
          const data = await getTyreOptionsApi();
          setTyreOptions(data.tyreOptions);
          setWheelOptions(data.wheelOptions);
          setClassificationOptions(data.classificationOptions);
          setBrandOptions(data.brandOptions);
          const sCdList: string[] = [];
          const sList: Supplier[] = [];
          data.classificationOptions.forEach(c => {
            if ((c.name === "タイヤ" || c.name === "ホイール") && c.suppliers) {
              c.suppliers.forEach((s) => {
                if (!sCdList.includes(s.supplierCd)) {
                  sList.push(s);
                  sCdList.push(s.supplierCd);
                }
              })
            }
          });
          setSupplierOptions(sList);
          setOrderStatusOptions(data.statusOptions);
        } catch (err) {
          console.error(err);
        } finally {
          setIsLoading(false);
        }
      })();
    }
  }, []);

  return [
    {
      isCustomerOrder,
      shopName,
      orderStatus,
      orderReceiptStatus,
      storeOrderer,
      approver,
      orderStartDate,
      orderEndDate,
      companyCdField,
      companyName,
      centerCd,
      orderNumber,
      productCd,
      productName,
      brand,
      janCd,
      productNumber,
      tyreWidth,
      tyreRatio,
      tyreRimDiam,
      wheelWidth,
      wheelPcd,
      wheelHole,
      wheelRimDiam,
      tyreWidthCd,
      tyreRatioCd,
      tyreRimDiamCd,
      wheelWidthCd,
      wheelPcdCd,
      wheelHoleCd,
      wheelRimDiamCd,
      classification,
      storehouse,
      supplier,
      shopCd,
    },
    {
      setIsCustomerOrder,
      setShopName,
      setOrderStatus,
      setOrderReceiptStatus,
      setStoreOrderer,
      setApprover,
      setOrderStartDate,
      setOrderEndDate,
      setCompanyCdField,
      setCompanyName,
      setCenterCd,
      setOrderNumber,
      setProductCd,
      setProductName,
      setBrand,
      setJanCd,
      setProductNumber,
      setTyreWidth,
      setTyreRatio,
      setTyreRimDiam,
      setWheelWidth,
      setWheelPcd,
      setWheelHole,
      setWheelRimDiam,
      setTyreWidthCd,
      setTyreRatioCd,
      setTyreRimDiamCd,
      setWheelWidthCd,
      setWheelPcdCd,
      setWheelHoleCd,
      setWheelRimDiamCd,
      setClassification,
      setStorehouse,
      setSupplier,
      setShopCd,
    },
    {
      classificationOptions,
      tyreOptions,
      wheelOptions,
      brandOptions,
      supplierOptions,
      orderStatusOptions,
    },
    { isLoading },
  ] as const;
}
